import { useState, useEffect } from 'react';
import { CheckCircle } from '@phosphor-icons/react';
import { getInitialBundle } from '../lib/versionCheck';

/**
 * Fresh-open notice: "Fore Tour has been updated (Build xxxxxxxx)"
 *
 * Shown once per new build when a device opens the app for the first time
 * on a build it hasn't seen before. Separate from the stale-tab UpdateBanner.
 *
 * Uses localStorage.ft_last_seen_build (cross-tab, persistent) so the notice
 * shows once per device per deploy, not once per tab.
 *
 * Guards:
 * - Only in production (process.env.NODE_ENV === 'production')
 * - Only when _initialBundle was captured (truthy, contains 8-char hex hash)
 */
export default function UpdatedNotice() {
  const [visible, setVisible] = useState(false);
  const [buildHash, setBuildHash] = useState('');

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    const bundle = getInitialBundle();
    if (!bundle) return;
    const m = bundle.match(/main\.([a-f0-9]{8,})\./i);
    if (!m) return;
    const hash = m[1].slice(0, 8);
    try {
      const lastSeen = localStorage.getItem('ft_last_seen_build');
      if (lastSeen === hash) return;
    } catch { return; }
    setBuildHash(hash);
    setVisible(true);
  }, []);

  const handleOk = () => {
    try {
      localStorage.setItem('ft_last_seen_build', buildHash);
    } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="updated-notice"
      className="fixed top-0 left-0 right-0 z-[9998] flex items-center justify-center gap-3 bg-[#1D242B] text-white px-4 py-3 shadow-lg"
    >
      <CheckCircle className="h-5 w-5 flex-shrink-0 text-[#4CAF50]" weight="fill" />
      <span className="text-sm font-semibold">Fore Tour has been updated (Build {buildHash})</span>
      <button
        onClick={handleOk}
        data-testid="updated-notice-ok"
        className="ml-2 px-3 py-1 text-xs font-bold rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
      >
        OK
      </button>
    </div>
  );
}
